import type { RuleExplanation } from '../api/client'
import type { Violation } from '../rules/types'
import catalogueDe from './catalogue.de.json'
import { fill } from './dict'
import { formats, activeLocale } from './locale'
import ruleDe from './rules.de.json'

/**
 * The pre-trade rules, in the trader's language.
 *
 * These sentences are written in C# — `PreTradeRules.Default` derives each one from the
 * account's own numbers, and the spec that carries it is persisted in `rule_config`, versioned
 * and governed by the config time-lock. Putting a locale inside it would mean that saving an
 * unrelated limit under a different UI language mints a new, time-locked configuration version
 * whose only difference is prose, and that one account's stored rules become unreadable to a
 * trader with a different language. So the server keeps writing English, and the translation
 * lives here, keyed by rule id.
 *
 * English is never restated: for `en` this returns the server's own words, which are the
 * authoritative ones. Only non-English bundles exist, and a rule the bundle has not got yet
 * falls back to the English rather than to a key.
 *
 * What the sentences need is already on the wire. Only one of the twelve interpolates
 * anything — `pretrade.max-risk-per-trade` names the limit — and the interpreter already
 * carries it as `Violation.limit` (`interpreter.ts`, `explain`). No server change, no new
 * field, no `Accept-Language`.
 *
 * Coverage in both directions is checked from C#, where the rule ids live; a TypeScript test
 * could only compare this file against itself.
 */
const BUNDLES: Partial<Record<string, Readonly<Record<string, string>>>> = { de: ruleDe }

export function ruleMessage(violation: Pick<Violation, 'rule' | 'message' | 'limit'>): string {
  const { locale } = activeLocale()
  const translated = locale === 'en' ? undefined : BUNDLES[locale]?.[violation.rule]
  if (translated === undefined) return violation.message
  return fill(translated, { limit: formats().upTo2.format(violation.limit) })
}

/**
 * The rule catalogue, in the trader's language.
 *
 * Same arrangement as the messages above and for the same reason: `RuleCatalogue.cs` lives
 * beside the rules so the description cannot drift from the behaviour, and the API serves it.
 * Only non-English is carried here, so there is never a second English to keep in step.
 *
 * The fallback is **per field**, not per rule. A half-translated entry should read as three
 * German lines and one English one rather than four English ones — and the `Does` line is the
 * one that matters: a trader who does not know whether a rule closes their position, locks the
 * account or merely tells them cannot consent to it being switched to enforce, and that consent
 * is the whole observe-to-enforce rollout.
 */
type CataloguePatch = Partial<Pick<RuleExplanation, 'title' | 'watches' | 'does' | 'why'>>

const CATALOGUES: Partial<Record<string, Readonly<Record<string, CataloguePatch>>>> = { de: catalogueDe }

export function explained(entry: RuleExplanation): RuleExplanation {
  const { locale } = activeLocale()
  if (locale === 'en') return entry

  const patch = CATALOGUES[locale]?.[entry.id]
  if (patch === undefined) return entry

  return {
    id: entry.id,
    title: patch.title || entry.title,
    watches: patch.watches || entry.watches,
    does: patch.does || entry.does,
    why: patch.why || entry.why,
  }
}
